import React, { useCallback, useMemo, useState } from 'react'
import { Stack } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { SystemMessage } from 'components/common/SystemMessage'
import type { FileType } from 'constants/misc'
import { fileIsImage } from 'utils/typeguards'
import { getImageQuality, ImageQuality } from 'utils/validators'
import { logger } from 'utils/logging'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { PfInlineLoading } from '../PfInlineLoading.component'
import { forProductCode, isOwnedByOtherLine, usePfV2VisualUpload } from '../../fileHub'
import { usePfStore } from '../../store'
import { EDITING_FILE_TYPES } from './uploadFileTypes'

interface Props {
  /**
   * FE product ID uploads are grouped under. Must match
   * `STAGING_SENTINEL_PRODUCT_ID` in `StagingAdapter.component.tsx` so
   * `useStagingRoomFeed` can resolve the room's `productId → kind` mapping.
   */
  uploadProductId: number
  /**
   * REAL legacy numeric product id of the selected variant, sent to the BE on
   * the upload-URL request (the BE 404s on the FE sentinel). `null` when the
   * variant's productCode carries no numeric id — uploads then fail cleanly
   * with an error chip.
   */
  productCode: string | null
  /**
   * Product-GROUP code the uploads are grouped under in the shared FileAPI scope.
   * Not the variant: switching variant inside one product must not hide the files
   * already uploaded for it.
   */
  scopeKey: string | null
  /**
   * Filenames owned by OTHER cart lines. The scope is grouped per product, so a
   * second line of the same product would otherwise re-offer (and re-attach) the
   * first line's photos.
   */
  claimedByOtherLines?: ReadonlySet<string>
  /**
   * The cart line uploading. Stamped on each upload so a file still uploading when the client switches to
   * another line of the same product stays with the line it was dropped on.
   */
  ownerId?: string
  /** The file types this upload accepts; editing visuals unless the product takes something else (`uploadFileTypes`). */
  fileTypes?: FileType[]
  /**
   * Staging needs a usable source image, as in legacy: an image under 1500 px on its longer side is refused,
   * and one under 1920 px is uploaded with a warning.
   */
  checksImageQuality?: boolean
}

/**
 * Drawer upload section — wraps the shared `FileAPIUpload` UI primitive and is
 * backed entirely by the detached PF-v2 visual FileAPI subsystem
 * (`usePfV2VisualUpload`, owned by `PfV2VisualUploadProvider` at the PF-v2 root).
 *
 * Files are eagerly uploaded on drop via the real FileAPI transport (signed URL
 * → GCS PUT → RAW_WEB derivative poll). The rendered listing reads the live
 * `FileAPIEntry[]` — so progress, errors and the generated preview are the real
 * upload state, not a synthetic stand-in. The staging configuration step's
 * `useStagingRoomFeed` reads the same scope to build rooms once derivatives are
 * ready.
 *
 * Session id and admin email are resolved live inside the provider's resolvers,
 * so this component only needs the product id to key the upload under.
 *
 * `productCode` is not only sent to the BE — it is the SCOPE the listing is read
 * through. The FileAPI scope lives for the whole PF-v2 session and is shared with
 * the configuration step, so a component speaking for one product must narrow it
 * (`forProductCode`) or it shows other products' uploads.
 *
 * Decision p55-5-01: `FileAPIUpload` from `components/common/FileAPIUpload` is
 * the canonical shared upload UI primitive for revamp surfaces.
 */
export const FileUploadSection: React.FC<Props> = ({
  uploadProductId,
  productCode,
  scopeKey,
  claimedByOtherLines,
  ownerId,
  fileTypes = EDITING_FILE_TYPES,
  checksImageQuality = false,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'purchase_flow', 'order'])
  const { stagingEntries, uploadVisuals, deleteVisual } = usePfV2VisualUpload()
  const unclaimAttachment = usePfStore((state) => state.unclaimAttachment)

  // ONE FileAPI scope serves the whole flow, so the listing has to be narrowed to
  // the product this drawer is configuring. Reading it raw showed every product
  // the files uploaded for every other one.
  const ownEntries = useMemo(
    () => forProductCode(stagingEntries, scopeKey)
      .filter((entry) => !claimedByOtherLines?.has(entry.gcFilename ?? ''))
      .filter((entry) => !ownerId || !isOwnedByOtherLine(entry, ownerId)),
    [stagingEntries, scopeKey, claimedByOtherLines, ownerId]
  )

  const trackAnalytics = usePfRevampAnalytics()
  const [refusedFilenames, setRefusedFilenames] = useState<string[]>([])
  const [lowQualityFilenames, setLowQualityFilenames] = useState<string[]>([])
  // Drops still being read for their size; the dropzone shows nothing until a checked file starts uploading.
  const [checkingDrops, setCheckingDrops] = useState(0)

  const handleUpload = useCallback(async (accepted: File[]) => {
    if (!checksImageQuality) {
      uploadVisuals(accepted, uploadProductId, productCode, scopeKey, ownerId)
      return
    }
    setCheckingDrops((count) => count + 1)
    // One file at a time, as legacy did: each check reads the whole image into memory.
    const rated: Array<{ file: File, quality: ImageQuality }> = []
    try {
      for (const file of accepted) {
        rated.push({ file, quality: fileIsImage(file) ? await getImageQuality(file) : ImageQuality.GOOD })
      }
    }
    finally {
      setCheckingDrops((count) => count - 1)
    }
    const named = (quality: ImageQuality) => rated.filter((entry) => entry.quality === quality).map(({ file }) => file.name)
    const refused = named(ImageQuality.INSUFFICIENT)
    const lowQuality = named(ImageQuality.BORDERLINE)
    const unreadable = named(ImageQuality.UNREADABLE)

    // A file the browser cannot decode (e.g. camera RAW) has no size to judge, so it goes through as before.
    if (unreadable.length) {
      logger.warn('pf_revamp staging source: image size unreadable, uploaded unchecked', undefined, { context: { component: 'FileUploadSection', action: 'check_image_quality', metadata: { productCode, count: unreadable.length } } })
    }
    if (refused.length || lowQuality.length || unreadable.length) {
      trackAnalytics({
        type: PfRevampEventName.SOURCE_IMAGE_QUALITY_CHECKED,
        payload: { refusedCount: refused.length, lowQualityCount: lowQuality.length, unreadableCount: unreadable.length },
      })
    }
    // Warnings add up across drops, as in legacy: a later drop must not hide an earlier file's warning. A file
    // dropped again under the same name is judged afresh, so its old warning goes.
    const redropped = new Set(rated.map(({ file }) => file.name))
    const updating = (names: string[]) => (current: string[]) => [...current.filter((name) => !redropped.has(name)), ...names]
    setRefusedFilenames(updating(refused))
    setLowQualityFilenames(updating(lowQuality))

    const usable = rated.filter(({ quality }) => quality !== ImageQuality.INSUFFICIENT).map(({ file }) => file)
    if (usable.length) uploadVisuals(usable, uploadProductId, productCode, scopeKey, ownerId)
  }, [checksImageQuality, uploadVisuals, uploadProductId, productCode, scopeKey, ownerId, trackAnalytics])

  const handleDelete = useCallback((entryId: string) => {
    // A line keeps owning its files until the drawer is saved, and a resumed
    // line's files are re-derived from that ownership — so a deletion that only
    // removed the scope entry came straight back, and the file was undeletable.
    const entry = stagingEntries.find((candidate) => candidate.id === entryId)
    if (entry?.gcFilename) unclaimAttachment(entry.gcFilename)
    // A deleted file is no longer one to warn about.
    if (entry?.originalFilename) setLowQualityFilenames((names) => names.filter((name) => name !== entry.originalFilename))
    deleteVisual(entryId)
  }, [deleteVisual, stagingEntries, unclaimAttachment])

  return (
    <Stack gap={1.2}>

      <FileAPIUpload
        files={ownEntries}
        dropzoneText={t('purchase_flow_revamp:drawer.upload_dropzone_idle')}
        uploadHandler={(files) => { void handleUpload(files) }}
        onDelete={handleDelete}
        onCancelUpload={handleDelete}
        acceptedFileTypes={fileTypes}
        onCheckFileSize={(size) => size > 0}
        showFileListing
      />

      {checkingDrops > 0 &&
        <PfInlineLoading size="xs" message={t('purchase_flow_revamp:drawer.checking_image_size')} />
      }

      {refusedFilenames.length > 0 &&
        <SystemMessage
          variant="error"
          title={t('purchase_flow:image_quality_error.title')}
          isRawContent
          message={<>
            <Trans t={t} i18nKey="purchase_flow:image_quality_error.description" />
            {/* Plain text: a file name is not markup. */}
            {` (${refusedFilenames.join(', ')})`}
          </>}
        />
      }

      {lowQualityFilenames.length > 0 &&
        <SystemMessage
          variant="warning"
          title={t('order:step_product.upload_visuals_low_quality_warning.title')}
          isRawContent
          message={<>
            <Trans t={t} i18nKey="order:step_product.upload_visuals_low_quality_warning.text" />
            {` (${lowQualityFilenames.join(', ')})`}
          </>}
        />
      }

    </Stack>
  )
}
